import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { throttle } from 'lodash';


import OrderSummary from '../components/checkout/organisms/OrderSummary';
import ContactInfo from '../components/checkout/organisms/ContatctInfo';
import Delivery from '../components/checkout/organisms/Delivery';
import Payment from '../components/checkout/organisms/Payment';

// Hooks
import { useCart } from '../contexts/CartContext';
import LogoNavigation from '../components/commons/organisms/LogoNavigation';
import APIService from '../utils/api';
import { useNavigate, useRevalidator } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { normalizePhoneNumber } from '../utils';
import usePageView from '../hooks/use-page-view';
import { MIXPANEL_ACTION, MIXPANEL_PAGE, ORDER_STATUS } from '../utils/constants';
import MixpanelService from '../utils/mixpanel';


interface CheckoutInterface {}

const Checkout = ({}: CheckoutInterface) => {
  
  const navigate = useNavigate();

  usePageView(MIXPANEL_PAGE.CHECKOUT);

  const {
    clearCart,
  } = useCart();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    setValue,
  } = useForm({
    mode: 'onChange', 
  });

  const {
    cart:products
  } = useCart();

  const {
    user
  } = useAuth();

  const country = watch('country');

  const totalAmount = useMemo(() => {
    let amount = 0;
      products.forEach((product) => product.units.forEach((unit) => {
        if (Number(unit.quantity) > 0) {
          amount += (Number(unit.quantity) * Number(unit.price));
        }
      }))
      return amount;
  }, [products]);

  const paypalExpressTotalAmount = useMemo(() => {
    /*
    Description	Amount
    Gross payment (you paid)	₱259.00
    PayPal fee (4.4%)	₱11.40
    Fixed fee	₱15.00
    Total fee	₱26.40
    */
    let amount = 0;
    products.forEach((product) => product.units.forEach((unit) => {
      if (Number(unit.quantity) > 0) {
        amount += (Number(unit.quantity) * Number(unit.price));
      }
    }))
    if (country !== 'Philippines') {
      amount += (amount * 0.044) + 18; // International fixed rate US
    } else {
      amount += (amount * 0.044) + 15; // PH Fixed Rate
    }
    return amount;
  }, [products, country]);

  // APIs
  const submitGuestOrder = async (payload: any) => {
    try {
      let data = await APIService.submitGuestOrder(payload);
      if (data.PONumber) {
        clearCart();
        navigate(`/order-success/${data.PONumber}`);
      }
    } catch (e) {
      console.log('err', e)
    }
  }

  // APIs
  const submitOrder = async (id: string, payload: any) => {
    try {
      let data = await APIService.submitOrder(id, payload);
      if (data.PONumber) {
        clearCart();
        navigate(`/order-success/${data.PONumber}`);
      }
    } catch (e) {
      console.log('err', e)
    }
  }

  const throttledSubmitOrder = useMemo(() => throttle((payload: any) => {
    MixpanelService.track(`${MIXPANEL_PAGE.CHECKOUT} - ${MIXPANEL_ACTION.BUTTON_CLICK}`, {
      item: 'Checkout',
      isGuest: user? false: true,
      paymentType: payload.paymentType
    });
    
    if (user) {
      submitOrder(user._id, payload);
    } else {
      submitGuestOrder(payload);
    }
  }, 2000), [user]); 

  const onSubmit = (data: any, params: {
    method: string;
    transactionId?: string;
  }) => {
    let payload: any = {
      items: products,
      address: {
        country: data.country,
        street: data.street,
        town: data.city,
        province: data.province,
        postCode: data.zip
      },
      totalPrice: totalAmount,
      paymentType: params.method
    };
    if (params.method === 'Express Checkout') {
      payload.paymentId = params.transactionId; 
      payload.totalPrice = paypalExpressTotalAmount;
      payload.isExpressCheckout = true;
      payload.status = ORDER_STATUS.PAYMENT_RECEIVED;
    }

    if (user) {
      payload = {
        ...payload,
        firstName: data.firstName,
        lastName: data.lastName,
        mobile: data.mobile,
      }
    } else {
      payload = {
        ...payload,
        guest:{
          firstName: data.firstName,
          lastName: data.lastName,
          mobile: data.mobile,
          email: data.email
        }
      }
    }
    throttledSubmitOrder(payload)
  };

  const handleFormSubmit = (params: any) => {
    handleSubmit((data) => onSubmit(data, params))(); // ← call it here
  };

  const handleOnExpressCheckout = (transactionId: string) => {
    handleSubmit((data) => onSubmit(data, { method: 'Express Checkout', transactionId }))();
  }

  const populateFields = () => {
    setValue('email', user?.email, { shouldValidate: true });
    setValue('mobile', user?.mobile ? normalizePhoneNumber(user?.mobile) : '', { shouldValidate: true });
    setValue('lastName', user?.lastName, { shouldValidate: true });
    setValue('firstName', user?.firstName, { shouldValidate: true });
    setValue('country', user?.address?.country, { shouldValidate: true });
    setValue('street', user?.address?.street, { shouldValidate: true });
    setValue('city', user?.address?.town, { shouldValidate: true });
    setValue('province', user?.address?.province, { shouldValidate: true });
    setValue('zip', user?.address?.postCode, { shouldValidate: true });
  };

  useEffect(() => {
    if (user) {
       populateFields();
    }
  }, [user])

  return (
    <>
      <LogoNavigation />
      <Layout>
        <OrderSummaryInfo>
          <OrderSummary
            products={products}
            totalAmount={totalAmount}
            shippingAmount={0}
          />
        </OrderSummaryInfo>
        
        <OrderInformation>
          <ContactInfo 
            register={register} 
            errors={errors} 
            trigger={trigger} 
            setValue={setValue} 
            watch={watch}
          />
          <Delivery register={register} errors={errors} />
          <Payment 
            paypalExpressAmount={paypalExpressTotalAmount}
            isFormValid={isValid}
            onPress={(method) => handleFormSubmit({ method })}
            onExpressCheckout={handleOnExpressCheckout}
          />
        </OrderInformation>
      </Layout>
    </>
  )
} 

const OrderInformation = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0rem 1rem;
`;

const OrderSummaryInfo = styled.div`
  flex: 1;
`;


const Layout = styled.div`
  display: flex;
  flex-direction: column;

  @media(min-width: 768px) {
    max-width: 1200px; /* or 1280px */
    margin: 0 auto;
    padding: 1rem 1rem; /* optional side padding */
    flex-direction: row;
  }
`;


export default Checkout;